/**
 * Step two of "Propose in bulk": the per-role list.
 *
 * The shared fields — title, role, department, hiring manager, unit, reason, cost category and the
 * rationale — were filled in on step one and are shown here read-only. What is left is the two
 * things that genuinely differ between otherwise identical roles: `Track and Level` and the
 * planned start date. Everything travels to the proxy ONCE as `shared`, so the rows of one
 * submission cannot disagree about anything but those two.
 *
 * ⚠️ **The rows live in the PARENT**, not here. This component is conditionally rendered, so state
 * held locally would be discarded every time someone went back to the shared fields — and going
 * back is exactly what the shared-error banner tells them to do, after the rows are filled. The
 * rules `blankSharedFields` deliberately does not check (length caps, `CAC`, an unknown hiring
 * manager) are all first reported at that point, so twenty dates would be lost to a typo in the
 * title.
 *
 * ⚠️ Airtable takes ten records per write and has no transaction, so a submission past ten roles
 * can half-succeed. That is reported as its own state here (`partial`) rather than as a failure,
 * because the ordinary reaction to a failure — press it again — would duplicate every role that
 * already landed.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  BulkPartialFailureError,
  BulkProposalValidationError,
  createProposedRoles,
  type NotifyOutcome,
  type BulkProposalRow,
  type SharedProposalInput,
} from '../../services/internalPlaygroundApi';
import { CompensationLine } from '../../components/CompensationSummary';
import { formatEuro } from '../../types/compensation';
import { useRoleCompensation } from './useRoleCompensation';
import { FIELD_CLASS, LABEL_CLASS, MAX_BULK_ROLES, resizeBulkRows } from './proposeRole';

const BLANK_ROW: BulkProposalRow = { trackAndLevel: '', plannedStartDate: '' };

type RowErrors = Record<number, Partial<Record<keyof BulkProposalRow, string>>>;

export function BulkProposalStep({
  shared,
  summary,
  rows,
  onRowsChange,
  trackOptions,
  onBack,
  onClose,
  onProposed,
}: {
  shared: SharedProposalInput;
  /** The step-one values, for the read-only recap. Built by the caller because only it knows how
   * to turn a hiring-manager employee id back into a name. */
  summary: { label: string; value: string; wide?: boolean }[];
  /** Held by the parent so they survive "Back to the shared fields" — see the note above. */
  rows: BulkProposalRow[];
  onRowsChange: (next: BulkProposalRow[]) => void;
  trackOptions: string[];
  onBack: () => void;
  onClose: () => void;
  /** The Slack outcome, reported to the page so it can warn that nobody was told. */
  onProposed?: (notify: NotifyOutcome, priorityDropped?: boolean) => void;
}) {
  /**
   * What the count box SHOWS, which is not always what the list holds.
   *
   * Binding the input straight to `rows.length` made it impossible to type a two-digit count:
   * clearing the field is a change to `''`, the list snapped back to its minimum of one, and the
   * next keystroke landed beside that `1` — typing "3" after a clear produced thirteen rows. So
   * the text is its own state, the list only follows a value that parses, and `onBlur` puts the
   * real count back when someone leaves the field empty or past the ceiling.
   */
  const [countText, setCountText] = useState(String(rows.length));

  /** Every row priced in one call — the rows share a Role and a department and differ only by the
   * two fields below, which is exactly the shape `useRoleCompensation` takes. */
  const compensation = useRoleCompensation(shared.role, shared.peopleTeam, rows);
  /**
   * ⚠️ Prices are joined to rows by INDEX, and `keepPreviousData` deliberately holds the last
   * answer while a new one is in flight — so between deleting row 1 of three and the refetch
   * landing, index 1 would render the DELETED row's salary under the row that took its place. The
   * length check is what makes the join safe: a stale answer is simply not shown.
   */
  const priced = compensation.rows.length === rows.length ? compensation.rows : [];
  const [rowErrors, setRowErrors] = useState<RowErrors>({});
  const [partial, setPartial] = useState<{ message: string; created: number; confirmed: boolean } | null>(
    null
  );
  const queryClient = useQueryClient();

  const invalidateTimeline = () =>
    void queryClient.invalidateQueries({ queryKey: ['internal-playground-hiring-timeline'] });

  const mutation = useMutation({
    mutationFn: () => createProposedRoles({ shared, roles: rows }),
    onSuccess: (result) => {
      invalidateTimeline();
      onProposed?.(result.notify, result.priorityDropped);
      onClose();
    },
    onError: (error) => {
      if (error instanceof BulkProposalValidationError) {
        setRowErrors(error.details.rows || {});
        return;
      }
      if (error instanceof BulkPartialFailureError) {
        // Some rows exist in Airtable now, so the timeline is already out of date — refresh it
        // even though this is the failure branch.
        invalidateTimeline();
        setPartial({
          message: error.message,
          created: error.createdIds.length,
          confirmed: error.confirmed,
        });
        return;
      }
      setRowErrors({});
    },
  });

  /**
   * Every path that changes the LENGTH of the list goes through here, so the count box cannot
   * drift from it — and so `rowErrors` is cleared exactly once, in one place.
   *
   * ⚠️ Those errors are keyed by INDEX, so any resize silently re-points them at the wrong rows:
   * shrink to two and grow back to five, and row five is a copy of row two carrying row five's old
   * "date is missing" under a filled date field. Cleared rather than re-indexed — the next submit
   * re-reports whatever is still wrong.
   */
  const applyRows = (next: BulkProposalRow[]) => {
    onRowsChange(next);
    setCountText(String(next.length));
    setRowErrors({});
  };

  // Editing a cell does NOT resize, so it keeps the other rows' errors and clears only its own.
  const setRow = (index: number, key: keyof BulkProposalRow, value: string) => {
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    setRowErrors((prev) => (prev[index]?.[key] ? { ...prev, [index]: { ...prev[index], [key]: undefined } } : prev));
  };

  const removeRow = (index: number) => {
    if (rows.length === 1) return;
    applyRows(rows.filter((_, i) => i !== index));
  };

  const otherError =
    mutation.error &&
    !(mutation.error instanceof BulkProposalValidationError) &&
    !(mutation.error instanceof BulkPartialFailureError)
      ? mutation.error instanceof Error
        ? mutation.error.message
        : 'Could not file the proposals.'
      : null;

  const validationError =
    mutation.error instanceof BulkProposalValidationError ? mutation.error : null;
  // Derived, not held in state: the server is the only thing that produces these, and a copy in
  // state could render one submission's shared errors beside the next one's row errors.
  const sharedErrors = Object.values(validationError?.details.shared ?? {}).filter(Boolean) as string[];
  // A refusal with no field to mark — no rows, or more than the ceiling — says it in the message.
  const submissionMessage =
    validationError && sharedErrors.length === 0 && Object.keys(validationError.details.rows ?? {}).length === 0
      ? validationError.message
      : null;

  if (partial) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>
              {partial.confirmed
                ? `${partial.created} of ${rows.length} roles were filed.`
                : `At least ${partial.created} of ${rows.length} roles were filed.`}
            </strong>{' '}
            {partial.confirmed
              ? 'Airtable refused the rest, and it accepts ten records per write — so the ones that landed are really there.'
              : 'The connection dropped before Airtable answered, so the batch in flight may have been written too.'}{' '}
            Check the proposals table and propose only what is missing; submitting this form again
            would file what is already there a second time.
            <span className="mt-1 block text-xs text-red-700">{partial.message}</span>
          </span>
        </div>
        <div className="flex justify-end">
          <Button type="button" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        // ⚠️ The DS `Button`'s `loading` only adds `pointer-events-none` and `aria-busy` — it never
        // sets `disabled`, so it stops the mouse and nothing else. Enter in the number box is an
        // implicit submit and reaches this handler regardless, and `mutate()` does not dedupe. Two
        // presses would file every role twice, which is the exact duplication the 429-only retry
        // rule and the partial-failure card exist to prevent.
        if (mutation.isPending) return;
        mutation.mutate();
      }}
    >
      <div className="rounded border border-zinc-200 bg-zinc-50 p-3">
        <p className="mb-2 text-xs font-medium text-zinc-600">Every role below is filed with</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {summary.map((entry) => (
            <div key={entry.label} className={`flex gap-1.5 ${entry.wide ? 'col-span-2' : ''}`}>
              <dt className="shrink-0 text-zinc-500">{entry.label}:</dt>
              <dd className="truncate text-zinc-900" title={entry.value}>
                {entry.value || '—'}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {sharedErrors.length > 0 && (
        <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Go back and fix the shared fields: {sharedErrors.join(' ')}
          </span>
        </div>
      )}

      <div className="flex items-end gap-3">
        <div className="w-36">
          <label className={LABEL_CLASS} htmlFor="bulk-count">
            Number of roles
          </label>
          <input
            id="bulk-count"
            type="number"
            min={1}
            max={MAX_BULK_ROLES}
            className={FIELD_CLASS}
            value={countText}
            onChange={(e) => {
              const typed = e.target.value;
              // An empty or unparseable box leaves the list alone — see `countText`.
              const count = Number(e.target.value);
              if (e.target.value.trim() && Number.isFinite(count) && count >= 1) {
                const next = resizeBulkRows(rows, count, BLANK_ROW);
                if (next !== rows) applyRows(next);
              }
              // After applyRows, which normalises the text to the real length — the box must keep
              // showing what is being typed, or "25" is unreachable through "2".
              setCountText(typed);
            }}
            onBlur={() => setCountText(String(rows.length))}
          />
        </div>
        <p className="pb-2 text-xs text-zinc-500">
          Up to {MAX_BULK_ROLES}. A new row copies the one above it — set the track and date that
          differ.
        </p>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-[2rem_1fr_1fr_2rem] items-center gap-2 text-xs font-medium text-zinc-600">
          <span className="sr-only">Role number</span>
          <span>Track and level</span>
          <span>Planned start date</span>
          <span className="sr-only">Remove</span>
        </div>
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-[2rem_1fr_1fr_2rem] items-start gap-2">
            <span className="pt-2 text-xs tabular-nums text-zinc-400">{index + 1}</span>
            <div>
              <select
                aria-label={`Track and level for role ${index + 1}`}
                className={FIELD_CLASS}
                value={row.trackAndLevel ?? ''}
                onChange={(e) => setRow(index, 'trackAndLevel', e.target.value)}
                disabled={trackOptions.length === 0}
              >
                <option value="">—</option>
                {trackOptions.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
              {rowErrors[index]?.trackAndLevel && (
                <p className="mt-1 text-xs text-red-600">{rowErrors[index]?.trackAndLevel}</p>
              )}
            </div>
            <div>
              <input
                aria-label={`Planned start date for role ${index + 1}`}
                type="date"
                className={FIELD_CLASS}
                value={row.plannedStartDate}
                onChange={(e) => setRow(index, 'plannedStartDate', e.target.value)}
              />
              {rowErrors[index]?.plannedStartDate && (
                <p className="mt-1 text-xs text-red-600">{rowErrors[index]?.plannedStartDate}</p>
              )}
            </div>
            <Button
              type="button"
              variant="icon"
              size="sm"
              aria-label={`Remove role ${index + 1}`}
              disabled={rows.length === 1}
              onClick={() => removeRow(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            {/* One line per row rather than the two-figure panel the single form shows:
                twenty-five panels would bury the dates they annotate. Placed on its own grid line
                under the two controls it is derived from. */}
            <div className="col-span-2 col-start-2">
              <CompensationLine compensation={priced[index]} />
            </div>
          </div>
        ))}
      </div>

      {/* The number the submission is actually approved on, and the one the Slack announcement
          carries — so nobody files twenty-five roles and first meets the total in the channel.

          ⚠️ Rendered at €0 too. Gating on `> 0` hid the line entirely when every role started next
          January, which is exactly the "a missing figure reads as a free submission" failure the
          panel's own unavailable states exist to prevent. It hides only when there is no priced
          row to total at all. */}
      {priced.length > 0 && (
        <p className="text-xs text-zinc-600">
          <span className="text-zinc-500">
            Total fully loaded cost until 31 Dec {priced[0]?.year ?? new Date().getFullYear()}:{' '}
          </span>
          <span className="font-semibold tabular-nums">
            {formatEuro(priced.reduce((sum, row) => sum + (row.costUntilYearEnd ?? 0), 0))}
          </span>
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={rows.length >= MAX_BULK_ROLES}
        onClick={() => applyRows(resizeBulkRows(rows, rows.length + 1, BLANK_ROW))}
      >
        Add a role
      </Button>

      {(submissionMessage || otherError) && (
        <p className="text-xs text-red-600">{submissionMessage || otherError}</p>
      )}

      <div className="flex items-center justify-between pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to the shared fields
        </Button>
        <Button type="submit" size="sm" loading={mutation.isPending} disabled={mutation.isPending}>
          Propose {rows.length} {rows.length === 1 ? 'role' : 'roles'}
        </Button>
      </div>
    </form>
  );
}
